import { Repeat, TrendingUp, UserPlus, Users } from 'lucide-react';

import ChartCard from '@/features/reports/components/ChartCard';
import DemandBarChart from '@/features/reports/components/charts/DemandBarChart';
import RankBarChart from '@/features/reports/components/charts/RankBarChart';
import { SimpleKpi } from '@/features/reports/components/KpiTile';
import ReportTable from '@/features/reports/components/ReportTable';
import { useReportOutlet } from '@/features/reports/hooks/useReportOutlet';
import { useCustomerCohorts, useTopCustomers } from '@/features/reports/hooks/useReports';
import {
  formatDecimal,
  formatNumber,
  formatPercent,
  formatRangeCaption,
  formatToman,
  persianDigits,
} from '@/features/reports/lib/format';

/** Customer acquisition, repeat behaviour and the highest-spending customers. */
export default function CustomersReport() {
  const { query, filters } = useReportOutlet();

  const cohorts = useCustomerCohorts(query);
  const topCustomers = useTopCustomers(query, { limit: 20 });

  const cohortRows = cohorts.data?.rows ?? [];
  const topRows = topCustomers.data?.rows ?? [];

  const bestCohort = cohortRows.reduce(
    (best, row, index, list) => (row.revenue > (list[best]?.revenue ?? -1) ? index : best),
    -1
  );

  return (
    <div className="reports-page">
      <div className="report-section-head">
        <h2 className="section-title">مشتریان</h2>
        <p className="report-section-caption">
          {formatRangeCaption(filters.from, filters.to)} • دید پلتفرمی
        </p>
      </div>

      <div className="kpi-grid report-kpi-grid">
        <SimpleKpi
          title="مشتریان فعال"
          value={cohorts.data?.active_customers}
          format={formatNumber}
          icon={Users}
          accent="blue"
        />
        <SimpleKpi
          title="مشتریان جدید"
          value={cohorts.data?.new_customers}
          format={formatNumber}
          icon={UserPlus}
          accent="green"
          hint="نخستین سفارش آن‌ها در این بازه ثبت شده است"
        />
        <SimpleKpi
          title="مشتریان بازگشتی"
          value={cohorts.data?.returning_customers}
          format={formatNumber}
          icon={Repeat}
          accent="purple"
        />
        <SimpleKpi
          title="نرخ خرید دوباره"
          value={cohorts.data?.repeat_purchase_rate_pct}
          format={(value) => formatPercent(value)}
          icon={TrendingUp}
          accent="amber"
        />
      </div>

      <ChartCard
        title="کوهورت‌های جذب مشتری"
        subtitle="مشتریان بر پایه ماه نخستین خرید (تقویم هجری شمسی)"
        height={320}
        isLoading={cohorts.isLoading}
        isError={cohorts.isError}
        error={cohorts.error}
        isEmpty={!cohortRows.length}
      >
        <DemandBarChart
          points={cohortRows}
          valueKey="revenue"
          highlight={bestCohort >= 0 ? bestCohort : null}
          height={320}
          showShare={false}
        />
      </ChartCard>

      <ChartCard
        title="جدول کوهورت‌ها"
        subtitle="تعداد مشتری، سفارش، درآمد و نرخ بازگشت هر کوهورت"
        bodyClassName="report-card-body-table"
        isLoading={cohorts.isLoading}
        isError={cohorts.isError}
        error={cohorts.error}
        isEmpty={!cohortRows.length}
      >
        <ReportTable
          rows={cohortRows}
          rowKey={(row) => row.key}
          maxHeight={480}
          columns={[
            { key: 'label', header: 'کوهورت', render: (row) => persianDigits(row.label) },
            { key: 'customers', header: 'مشتری', render: (row) => formatNumber(row.customers) },
            { key: 'orders', header: 'سفارش', render: (row) => formatNumber(row.orders) },
            { key: 'revenue', header: 'درآمد', render: (row) => formatToman(row.revenue) },
            {
              key: 'repeat_customers',
              header: 'خرید دوباره',
              render: (row) => formatNumber(row.repeat_customers),
            },
            {
              key: 'repeat_rate_pct',
              header: 'نرخ بازگشت',
              render: (row) => formatDecimal(row.repeat_rate_pct, 1) + '٪',
            },
          ]}
        />
      </ChartCard>

      <div className="analytics-grid">
        <ChartCard
          title="مشتریان برتر بر پایه درآمد"
          subtitle="۲۰ مشتری نخست"
          height={420}
          isLoading={topCustomers.isLoading}
          isError={topCustomers.isError}
          error={topCustomers.error}
          isEmpty={!topRows.length}
        >
          <RankBarChart
            rows={topRows.map((row) => ({
              ...row,
              label: row.full_name || row.mobile_phone || `#${row.user_id}`,
            }))}
            valueKey="revenue"
            money
            height={420}
            yAxisWidth={150}
            showShare={false}
          />
        </ChartCard>

        <ChartCard
          title="جدول مشتریان برتر"
          subtitle="سفارش، درآمد و میانگین سفارش"
          bodyClassName="report-card-body-table"
          isLoading={topCustomers.isLoading}
          isError={topCustomers.isError}
          error={topCustomers.error}
          isEmpty={!topRows.length}
        >
          <ReportTable
            rows={topRows}
            rowKey={(row) => row.user_id}
            maxHeight={420}
            columns={[
              {
                key: 'full_name',
                header: 'مشتری',
                render: (row) => (
                  <div className="report-cell-stack">
                    <span className="report-cell-primary">{row.full_name || '—'}</span>
                    <span className="report-cell-secondary">{persianDigits(row.mobile_phone) || '—'}</span>
                  </div>
                ),
              },
              { key: 'orders', header: 'سفارش', render: (row) => formatNumber(row.orders) },
              { key: 'revenue', header: 'درآمد', render: (row) => formatToman(row.revenue) },
              { key: 'aov', header: 'میانگین سفارش', render: (row) => formatToman(row.aov) },
            ]}
          />
        </ChartCard>
      </div>
    </div>
  );
}
